import { UserProfile, SignOutButton } from "@clerk/react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleDashed, Building2, Mail, Users, PiggyBank, FileSpreadsheet, Tag, ShieldCheck } from "lucide-react";
import { useApi } from "@/lib/api";
import { MONTHS } from "@/lib/dates";
import InstallButton from "@/components/InstallButton";
import { Button } from "@/components/ui/button";
import { useMe, isManager } from "@/lib/me";
import { ROLE_LABEL } from "@/lib/format";
import { PageHead } from "@/components/Bits";

const Feature = ({ on, label, off, testid }) => (
  <div className="flex items-start gap-3 py-2" data-testid={testid}>
    {on ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" /> : <CircleDashed className="mt-0.5 h-5 w-5 text-slate-400" />}
    <div><p className="text-sm font-semibold">{label}</p><p className="text-xs text-slate-500">{on ? "On" : off}</p></div>
  </div>
);

function YearStart() {
  const api = useApi();
  const [m, setM] = useState(useMe().settings?.year_start_month || 4);
  const save = async (v) => {
    setM(v);
    try {
      await api("put", "/settings", { year_start_month: v });
      toast.success(`Year now starts in ${MONTHS[v - 1]} — reloading`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="mb-8 rounded-3xl border border-slate-200 bg-white p-5 rise" data-testid="year-start-setting">
      <h2 className="mb-1 text-lg font-bold">Financial year</h2>
      <p className="mb-3 text-sm text-slate-500">Quarters and "this year" in Spend and the date filters count from this month. UK tax year: April.</p>
      <select value={m} onChange={(e) => save(Number(e.target.value))} className="h-9 rounded-md border border-input bg-white px-3 text-sm" data-testid="year-start-select">
        {MONTHS.map((n, i) => <option key={n} value={i + 1}>Starts in {n}</option>)}
      </select>
    </div>
  );
}

export default function AccountPage() {
  const me = useMe();
  const f = me.features;
  return (
    <div>
      <PageHead title="Account" sub={`${me.email} · ${ROLE_LABEL[me.role]}`}>
        <SignOutButton><Button variant="outline" data-testid="signout-button">Sign out</Button></SignOutButton>
      </PageHead>
      <InstallButton className="mb-6 w-full md:hidden" />
      {isManager(me) && (
        <div className="mb-6 grid grid-cols-2 gap-2 md:hidden" data-testid="mobile-more-links">
          {[["/statements", "Statements", FileSpreadsheet], ["/prices", "Prices", Tag], ["/approvals", "Approvals", ShieldCheck], ["/wholesalers", "Wholesalers", Building2], ["/summary", "Summary", PiggyBank], ["/emails", "Emails", Mail], ["/team", "Team", Users]].map(([to, label, Icon]) => (
            <Link key={to} to={to} className="flex flex-col items-center gap-1 rounded-2xl border border-slate-200 bg-white py-4 text-sm font-semibold" data-testid={`more-${label.toLowerCase()}-link`}>
              <Icon className="h-5 w-5 text-teal-700" /> {label}
            </Link>
          ))}
        </div>
      )}
      {isManager(me) && (
        <div className="mb-8 rounded-3xl border border-slate-200 bg-white p-5 rise" data-testid="integration-status">
          <h2 className="mb-2 text-lg font-bold">Pharmacy setup</h2>
          <Feature on={f.email_sending} label={`Send emails from ${f.mailbox}`} off="Off — needs the Gmail app password. Emails are only logged." testid="feature-email" />
          <Feature on={f.inbox_reading} label="Read wholesaler replies from the inbox (hourly)" off="Off — needs the Gmail app password. Log replies by hand." testid="feature-inbox" />
          <Feature on={f.photo_reading} label="Read invoice photos with AI" off="Off — needs the Anthropic API key. Staff type details." testid="feature-photo" />
        </div>
      )}
      {me.role === "owner" && <YearStart />}
      <h2 className="mb-3 text-lg font-bold">Password & profile</h2>
      <div className="overflow-x-auto"><UserProfile routing="hash" /></div>
    </div>
  );
}
